// B-134 — an office's "Máquina GLORY" switch, shared by the Oficinas screen (a column, a line on the
// cards) and the closing detail ("Configuración de la oficina", in "Editar").
//
//  - `GloryCelda`: the switch, and beside it what it means — "Activa", or a chip with why it is off —
//    who changed it last and when, and the history. Everyone sees it; the switch moves only for
//    whoever holds `cierres.offices.configure` on the office (the server's `editable`), and is said
//    disabled otherwise. It is controlled by the server's answer: flipping it opens a dialog and
//    nothing changes on screen until the change is saved.
//  - `CambiarGloryDialog`: turning it off asks why — "Sin máquina", "Máquina dañada", or "Otro" with
//    the reason written; turning it on asks only to confirm, with an optional note. Both say it
//    applies from the next evaluation and that a past day takes it only when redone.
//  - `HistorialGloryDialog`: every change, newest first.
//
// Plain `Dialog`s, as the other Cierres confirmations are: `ConfirmDialog` needs a provider no screen
// here mounts. The words and the payload are `utils/cierreGlory.js`.
import { useEffect, useState } from "react";
import { History, Power, PowerOff } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  Spinner,
  Switch,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@mantis-sas/ui";

import {
  listCierreOfficeGlory,
  listCierreOfficeGloryHistory,
  saveCierreOfficeGlory,
} from "../../../ApiCalls/mantisBusiness";
import { sinPermiso } from "../../../utils/accesoDiccionario";
import { correctionError } from "../../../utils/cierreCorrections";
import {
  MAX_DETALLE_GLORY,
  MOTIVOS_GLORY,
  avisoDeCambio,
  cambioDeGlory,
  entradaDeHistorial,
  etiquetaMotivo,
  firmaCambio,
  motivoValido,
} from "../../../utils/cierreGlory";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

const PERMISO_GLORY = "cierres.offices.configure";
const DIAS_PASADOS = "Los días ya procesados no cambian; para aplicarlo a un día pasado, usa «Rehacer».";

const errorDe = (response) =>
  response?.errorCode || response?.message ? correctionError(response) : "No se pudo guardar el cambio.";

/**
 * The switch and its meaning. `estado` is one office of `listCierreOfficeGlory`. `soloLectura` draws
 * the meaning without the switch (the closing detail outside "Editar"). `sinFirma` leaves out the
 * "por X · fecha" line, so a table row keeps its height; the chip's tooltip and the history still say it.
 */
export function GloryCelda({ estado, soloLectura = false, sinFirma = false, onCambiar, onHistorial, className }) {
  if (!estado) return <span className="text-[12.5px] text-brinks-muted-foreground">—</span>;
  const encendida = estado.gloryEnabled !== false;
  const motivo = etiquetaMotivo(estado);
  const firma = firmaCambio(estado);
  const nombre = estado.officeName || estado.officeCode;

  const interruptor = (
    <Switch
      checked={encendida}
      disabled={!estado.editable}
      onCheckedChange={(valor) => onCambiar && onCambiar(estado, valor)}
      aria-label={`Máquina GLORY de ${nombre}`}
      className="shrink-0"
    />
  );

  return (
    <TooltipProvider delayDuration={150}>
      <div className={cn("min-w-0", className)}>
        <div className="flex min-w-0 items-center gap-2">
          {soloLectura ? null : estado.editable ? (
            interruptor
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* A disabled switch takes no pointer events, so the tooltip hangs on its wrapper. */}
                <span tabIndex={0} className="inline-flex shrink-0 rounded-full">
                  {interruptor}
                </span>
              </TooltipTrigger>
              <TooltipContent className="brinks-scope font-redhat max-w-[260px] text-[12px]">
                {sinPermiso(PERMISO_GLORY)}
              </TooltipContent>
            </Tooltip>
          )}
          {encendida ? (
            <span className="inline-flex min-w-0 items-center gap-1 text-[12.5px] text-brinks-muted-foreground">
              {soloLectura ? <Power className="h-3.5 w-3.5 shrink-0 text-brinks-success" /> : null}
              <span className="truncate">Activa</span>
            </span>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  className="inline-flex min-w-0 items-center gap-1 rounded-full border border-brinks-warning-border bg-brinks-warning-soft px-2 py-0.5 text-[11.5px] font-medium leading-tight text-brinks-warning"
                >
                  <PowerOff className="h-3 w-3 shrink-0" />
                  <span className="truncate">{motivo}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent className="brinks-scope font-redhat max-w-[280px] text-[12px]">
                <p className="font-medium">GLORY apagada: {motivo}</p>
                <p className="mt-0.5 text-brinks-muted-foreground">Su arqueo es el conteo manual firmado.</p>
                {firma ? <p className="mt-0.5 text-brinks-muted-foreground">{firma}</p> : null}
              </TooltipContent>
            </Tooltip>
          )}
          {onHistorial ? (
            <button
              type="button"
              onClick={() => onHistorial(estado)}
              aria-label={`Historial GLORY de ${nombre}`}
              title="Historial de cambios"
              className="grid h-6 w-6 shrink-0 place-items-center rounded-[6px] text-brinks-muted-foreground hover:bg-brinks-surface-hover hover:text-brinks-foreground"
            >
              <History className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
        {firma && !sinFirma ? <p className="mt-0.5 truncate text-[11px] text-brinks-muted-foreground" title={firma}>{firma}</p> : null}
      </div>
    </TooltipProvider>
  );
}

/**
 * Turn one office's GLORY off (`encender` false: a reason required) or on (a note, optional).
 * `oficina` is `{ officeCode, officeName }`; `onSaved` gets the backend's answer.
 */
export function CambiarGloryDialog({ oficina, encender, token, mantisBusinessId, clientCode, onClose, onSaved }) {
  const [reasonCode, setReasonCode] = useState("");
  const [note, setNote] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const listo = encender || motivoValido({ reasonCode, note });
  const nombre = `${oficina?.officeName || ""} (${oficina?.officeCode || ""})`;

  const guardar = async () => {
    setGuardando(true);
    setError("");
    const response = await saveCierreOfficeGlory(
      token,
      mantisBusinessId,
      clientCode,
      oficina.officeCode,
      cambioDeGlory({ encender, reasonCode, note })
    ).catch(() => null);
    setGuardando(false);
    if (response?.success) onSaved(response.data);
    else setError(errorDe(response));
  };

  const Icono = encender ? Power : PowerOff;

  return (
    <Dialog open={Boolean(oficina)} onOpenChange={(abierto) => !abierto && !guardando && onClose()}>
      <DialogContent className="brinks-scope font-redhat max-w-md">
        <DialogHeader className="text-left">
          <div className="flex items-start gap-3">
            <span
              className={cn(
                "grid h-9 w-9 shrink-0 place-items-center rounded-[8px] border",
                encender
                  ? "border-brinks-success-border bg-brinks-success-soft text-brinks-success"
                  : "border-brinks-warning-border bg-brinks-warning-soft text-brinks-warning"
              )}
            >
              <Icono className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1">
                <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">
                  {encender ? "Encender" : "Apagar"} la GLORY de {nombre}
                </DialogTitle>
                <BotonGuia guiaId="dialogo-cambiar-glory" titulo="Máquina GLORY" texto="Encendida, a la oficina se le pide y se le lee su GLORY. Apagada, no se le pide y su arqueo es el conteo manual firmado. Aplica desde la próxima evaluación del cierre." nombre="Máquina GLORY" />
              </div>
              <DialogDescription className="mt-1 text-[12.5px] text-brinks-muted-foreground">
                {encender
                  ? "Desde la próxima evaluación se le pedirá y leerá su GLORY."
                  : "Desde la próxima evaluación no se le pedirá la GLORY: su arqueo será el conteo manual firmado."}{" "}
                {DIAS_PASADOS}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {encender ? (
          <div className="grid gap-1.5">
            <Label htmlFor="nota-glory" className="text-[12px] font-medium text-brinks-muted-foreground">
              Nota (opcional)
            </Label>
            <Textarea
              {...guia("Nota", "Opcional: por qué se enciende. Queda en el historial de la máquina.")}
              id="nota-glory"
              rows={3}
              maxLength={MAX_DETALLE_GLORY}
              value={note}
              disabled={guardando}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Por ejemplo: máquina reparada"
              className="text-[12.5px]"
            />
          </div>
        ) : (
          <div className="grid gap-2">
            <Label className="text-[12px] font-medium text-brinks-muted-foreground">Motivo</Label>
            <RadioGroup {...guia("Motivo", "Por qué se apaga: sin máquina, máquina dañada u otro. \"Otro\" pide escribirlo.")} value={reasonCode} onValueChange={setReasonCode} disabled={guardando} className="grid gap-1.5">
              {MOTIVOS_GLORY.map((m) => (
                <label
                  key={m.codigo}
                  htmlFor={`motivo-glory-${m.codigo}`}
                  className={cn(
                    "flex cursor-pointer items-start gap-2.5 rounded-[8px] border px-3 py-2 transition",
                    reasonCode === m.codigo
                      ? "border-brinks-border-strong bg-brinks-surface"
                      : "hover:bg-brinks-surface-hover"
                  )}
                >
                  <RadioGroupItem id={`motivo-glory-${m.codigo}`} value={m.codigo} className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-brinks-foreground">{m.etiqueta}</span>
                    <span className="block text-[11.5px] text-brinks-muted-foreground">{m.ayuda}</span>
                  </span>
                </label>
              ))}
            </RadioGroup>
            {reasonCode === "OTHER" ? (
              <div className="grid gap-1">
                <Label htmlFor="detalle-glory" className="text-[12px] font-medium text-brinks-muted-foreground">
                  ¿Por qué se apaga? (obligatorio)
                </Label>
                <Textarea
                  id="detalle-glory"
                  rows={3}
                  autoFocus
                  maxLength={MAX_DETALLE_GLORY}
                  value={note}
                  disabled={guardando}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Escribe el motivo"
                  className="text-[12.5px]"
                />
              </div>
            ) : null}
          </div>
        )}

        {error ? (
          <p role="alert" className="text-[12px] text-brinks-danger">
            {error}
          </p>
        ) : null}
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-9" disabled={guardando} onClick={onClose}>
            Cancelar
          </Button>
          <Button {...guia("Guardar", "Guarda el cambio. Un día ya procesado conserva su lectura hasta que se rehace.")} className="h-9 gap-1.5" disabled={guardando || !listo} onClick={guardar}>
            {guardando ? <Spinner size="sm" /> : <Icono className="h-4 w-4" />}
            {encender ? "Encender GLORY" : "Apagar GLORY"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Historial({ oficina, token, mantisBusinessId, clientCode }) {
  const [cambios, setCambios] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let activo = true;
    (async () => {
      const response = await listCierreOfficeGloryHistory(token, mantisBusinessId, clientCode, oficina.officeCode).catch(
        () => null
      );
      if (!activo) return;
      if (!response?.success) {
        setError(response?.message ? correctionError(response) : "No se pudo cargar el historial.");
        setCambios([]);
        return;
      }
      setCambios(response.data?.changes || []);
    })();
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, clientCode, oficina.officeCode]);

  if (cambios === null) {
    return (
      <div className="grid place-items-center p-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="max-h-[65vh] space-y-2 overflow-y-auto p-4">
      {error ? (
        <p
          role="alert"
          className="rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-2.5 py-1.5 text-[12.5px] text-brinks-danger"
        >
          {error}
        </p>
      ) : null}
      {!error && cambios.length === 0 ? (
        <p className="text-[12.5px] text-brinks-muted-foreground">
          Nadie ha cambiado la GLORY de esta oficina: está encendida desde que se registró.
        </p>
      ) : null}
      <ol className="space-y-2">
        {cambios.map((cambio) => {
          const e = entradaDeHistorial(cambio);
          return (
            <li key={`${cambio.changedAt}-${cambio.enabled}`} className="space-y-1 rounded-[10px] border border-brinks-border bg-card p-3">
              <p className="text-[12px] text-brinks-muted-foreground">
                {e.cuando} · {e.autor}
              </p>
              <p
                className={cn(
                  "flex items-center gap-1.5 text-[12.5px] font-medium",
                  e.tono === "encendida" ? "text-brinks-success" : "text-brinks-warning"
                )}
              >
                {e.tono === "encendida" ? <Power className="h-3.5 w-3.5" /> : <PowerOff className="h-3.5 w-3.5" />}
                {e.titulo}
              </p>
              {e.detalle ? <p className="whitespace-pre-line text-[12.5px] text-brinks-foreground">{e.detalle}</p> : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * The switch's state for a client's offices (or one, `officeCode`), and the two dialogs — shared by
 * Oficinas and the closing detail so both read, change and say it the same way. A hook handing back
 * the dialogs, like `useAtenderAlerta`: the caller decides where the cells go, the dialogs mount once.
 * `onGuardado(aviso, respuesta)` hears each saved change; the state is asked again after it.
 */
export function useGloryOficinas({ token, mantisBusinessId, clientCode, listo = true, officeCode, onGuardado }) {
  const [respuesta, setRespuesta] = useState(null);
  const [version, setVersion] = useState(0);
  const [cambiando, setCambiando] = useState(null);
  const [historial, setHistorial] = useState(null);

  useEffect(() => {
    if (!listo || !clientCode) return undefined;
    let activo = true;
    (async () => {
      const r = await listCierreOfficeGlory(token, mantisBusinessId, clientCode, officeCode).catch(() => null);
      if (activo) setRespuesta(r?.success ? r.data : null);
    })();
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, clientCode, listo, officeCode, version]);

  const oficinas = respuesta?.supported ? respuesta.offices || [] : [];
  const gloryDe = new Map(oficinas.map((o) => [String(o.officeCode), o]));

  const dialogos = (
    <>
      {cambiando ? (
        <CambiarGloryDialog
          oficina={cambiando.estado}
          encender={cambiando.encender}
          token={token}
          mantisBusinessId={mantisBusinessId}
          clientCode={clientCode}
          onClose={() => setCambiando(null)}
          onSaved={(data) => {
            setCambiando(null);
            setVersion((n) => n + 1);
            if (onGuardado) onGuardado(avisoDeCambio(cambiando.estado, cambiando.encender), data);
          }}
        />
      ) : null}
      <HistorialGloryDialog
        oficina={historial}
        token={token}
        mantisBusinessId={mantisBusinessId}
        clientCode={clientCode}
        onClose={() => setHistorial(null)}
      />
    </>
  );

  return {
    soportado: Boolean(respuesta?.supported),
    gloryDe,
    cambiar: (estado, encender) => setCambiando({ estado, encender }),
    verHistorial: (estado) => setHistorial(estado),
    recargar: () => setVersion((n) => n + 1),
    dialogos,
  };
}

/** Every change of one office's switch. `oficina` is `{ officeCode, officeName }`; null closes it. */
export function HistorialGloryDialog({ oficina, token, mantisBusinessId, clientCode, onClose }) {
  return (
    <Dialog open={Boolean(oficina)} onOpenChange={(abierto) => !abierto && onClose()}>
      <DialogContent className="brinks-scope font-redhat max-h-[90vh] max-w-xl gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b p-4 text-left">
          <div className="flex min-w-0 items-center gap-1">
            <DialogTitle className="text-[16px] font-semibold">
              Historial de la GLORY de {oficina?.officeName} ({oficina?.officeCode})
            </DialogTitle>
            <BotonGuia guiaId="dialogo-historial-glory" titulo="Historial de la GLORY" texto="Cada vez que se encendió o apagó la máquina de esta oficina: quién, cuándo y por qué. “Carga inicial” es la lista que entregó operaciones." nombre="Historial de la GLORY" />
          </div>
        </DialogHeader>
        {oficina ? (
          <Historial oficina={oficina} token={token} mantisBusinessId={mantisBusinessId} clientCode={clientCode} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
